import { useEffect } from "react";
import { Link } from "react-router-dom";
import { RESUME_URL } from "@/data";

const HERO_IMAGE =
  "https://customer-assets-agu9un31.emergentagent.net/job_63deb20a-37d0-451f-872c-c4c59d1b6116/artifacts/f43c2rzk_Complex_News_Logo_Treatment%20%280-00-02-15%29.png";

// Direct YouTube URL to the award-winning episode. Update here to swap.
const EPISODE_URL =
  "https://www.youtube.com/results?search_query=Stop+Killing+Us+Black+Transgender+Women+Complex+News";

const RESPONSIBILITIES = [
  "Motion Design",
  "Motion Direction",
  "Compositing",
  "Data Visualization",
  "Charts & Informational Graphics",
  "Photo & Archival Image Treatments",
  "Video Treatments",
  "Typography & Title Animation",
  "Editorial Graphics",
  "VFX & Transitions",
];

export default function ComplexWorld() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -100px 0px" }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App thtl-page cw-page">
      {/* Top Nav */}
      <div className="cs-topnav" data-testid="cs-topnav">
        <Link to="/" className="cs-back" data-testid="cs-back-link">
          <span className="cs-back-arrow">←</span>
          <span>Dan McNamara</span>
        </Link>
        <div className="nav-links">
          <Link to="/#reel">Reel</Link>
          <Link to="/#recent-work">Portfolio Work</Link>
          <Link to="/#work">Selected Work</Link>
          <Link to="/#contact">Contact</Link>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-resume"
            data-testid="nav-resume"
          >
            Resume
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="thtl-hero" data-testid="cw-hero">
        <div className="thtl-hero-grid">
          <div className="thtl-hero-text">
            <p className="thtl-kicker reveal">
              <span className="thtl-pulse"></span>
              Case Study · Complex World
            </p>
            <h1 className="thtl-headline reveal" data-testid="cw-headline">
              Complex
              <br />
              World
            </h1>
            <p className="thtl-tagline reveal">
              Current Affairs Documentary Series
            </p>
            <div className="thtl-divider reveal"></div>
            <p className="thtl-summary reveal">
              Motion direction and visual language for{" "}
              <strong>Complex's</strong> current-affairs documentary series,
              exploring stories across politics, culture, and social issues —
              from the Flint water crisis and policing to environmental racism,
              political misinformation, voting, and immigration — through the
              perspective of Complex's audience.
            </p>
          </div>

          <div
            className="thtl-hero-art cw-hero-art reveal"
            aria-hidden="true"
          >
            <img src={HERO_IMAGE} alt="Complex World key art" />
          </div>
        </div>
      </section>

      {/* Meta strip */}
      <section className="thtl-meta-strip reveal" data-testid="cw-meta-strip">
        <div className="thtl-meta-grid">
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Client</span>
            <span className="thtl-meta-value">Complex</span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Role</span>
            <span className="thtl-meta-value">
              Motion Graphics Artist · Motion Direction
            </span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Series</span>
            <span className="thtl-meta-value">
              Complex News Presents / Complex World
            </span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Recognition</span>
            <span className="thtl-meta-value">
              2021 GLAAD Media Award Winner
            </span>
          </div>
        </div>
      </section>

      {/* Award callout — surfaced early for prominence */}
      <section
        className="cw-award reveal"
        data-testid="cw-award"
        aria-label="2021 GLAAD Media Award"
      >
        <div className="cw-award-inner">
          <p className="cw-award-kicker">2021 GLAAD Media Award Winner</p>
          <p className="cw-award-category">
            Outstanding Online Journalism — Video or Multimedia
          </p>
          <h2 className="cw-award-title">
            “Stop Killing Us: Black Transgender Women's Lived Experiences”
          </h2>
          <p className="cw-award-note">
            Recognized in a journalism category — not entertainment design —
            alongside nominees from Business Insider, Refinery29, Great Big
            Story, and Vox.
          </p>
          <a
            href={EPISODE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cw-award-cta"
            data-testid="cw-episode-link"
          >
            Watch the award-winning episode on YouTube
            <span className="cw-award-arrow" aria-hidden="true">
              →
            </span>
          </a>
        </div>
      </section>

      {/* Overview */}
      <section
        id="overview"
        className="thtl-section reveal"
        data-testid="cw-overview"
      >
        <div className="thtl-section-header">
          <span className="section-number">01</span>
          <h2 className="thtl-section-title">Overview</h2>
        </div>

        <div className="thtl-process-copy">
          <p className="thtl-lead">
            As the motion designer for the series, I developed the motion
            direction and visual language used to support each episode's
            storytelling. The work adapted to the subject matter rather than
            relying on a single graphics template.
          </p>
          <p>
            Working with producers and editors, I created the motion graphics
            and additional visual assets needed for individual stories —
            depending on the episode, that meant finding a clear way to
            present statistics and contextual information, bringing archival
            photographs to life, developing visual treatments for supplied
            footage, or building graphics from scratch when the story required
            them.
          </p>
          <p>
            The series required a careful balance between design and editorial
            clarity. Graphics needed to feel consistent with Complex while
            supporting serious documentary subjects — informative, direct, and
            never overwhelming the reporting.
          </p>
        </div>
      </section>

      {/* Role */}
      <section
        id="role"
        className="thtl-section thtl-role reveal"
        data-testid="cw-role"
      >
        <div className="thtl-section-header">
          <span className="section-number">02</span>
          <h2 className="thtl-section-title">Role</h2>
        </div>

        <div className="thtl-role-body">
          <div className="thtl-role-copy">
            <p>
              Motion Graphics Artist / Motion Direction. Across the series,
              that meant title and typography animation, charts and data
              visualization, informational graphics, photo and archival-image
              treatments, video treatments, compositing, transitions, and
              custom graphics built per-episode to visually communicate
              information.
            </p>
            <p>
              Each story set a different brief. A piece on misinformation
              needed clean typographic cuts and quick-read data; an archival
              recovery story needed careful photo treatments and restrained
              motion; a verité documentary piece needed graphics that stepped
              back so the subjects could lead.
            </p>
          </div>

          <ul
            className="thtl-contributions"
            aria-label="Responsibilities"
            data-testid="cw-contributions"
          >
            <li className="thtl-contributions-heading">Responsibilities</li>
            {RESPONSIBILITIES.map((c, i) => (
              <li key={i} className="thtl-contribution">
                <span className="thtl-contribution-dot" aria-hidden="true" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Outro */}
      <section className="thtl-outro reveal" data-testid="cw-outro">
        <div className="thtl-outro-inner">
          <h3 className="thtl-outro-title">
            Need motion direction for a documentary series or editorial
            program?
          </h3>
          <div className="thtl-outro-actions">
            <a
              href="mailto:dbmcnamara@gmail.com"
              className="thtl-outro-email"
              data-testid="cw-outro-email"
            >
              dbmcnamara@gmail.com
            </a>
            <Link
              to="/#recent-work"
              className="thtl-outro-back"
              data-testid="cw-outro-back-link"
            >
              ← Back to Portfolio Work
            </Link>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <p>© 2026 Dan McNamara. Available for full-time and freelance work.</p>
      </footer>
    </div>
  );
}
